'use client';

import React, { use } from 'react';
import { SchoolUnit, validUnits, getUnitFinancialStats, getSchoolName, getUnitNeraca } from '@/lib/data';
import { NeracaItem } from '@/lib/data/types';
import { notFound } from 'next/navigation';
import { Calculator, ShieldCheck, Wallet, Landmark, Building, Layers, Calendar, ChevronDown, CheckCircle2, AlertCircle, Building2 } from 'lucide-react';

export default function NeracaLaporanPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);
    const [activeUnit, setActiveUnit] = React.useState<SchoolUnit>(initialUnit as SchoolUnit);
    const [selectedDate, setSelectedDate] = React.useState('2026-02-28');
    const [quickFilter, setQuickFilter] = React.useState('none');

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const neracaData = getUnitNeraca(activeUnit);

    // Group units by base type (paud, sd, smp, smk)
    const baseType = activeUnit.split('-')[0];
    const availableUnits = validUnits.filter(u => u.startsWith(baseType) && u.includes('-'));

    // Helpers for currency
    const parseCurrency = (val: string) => parseInt(val.replace(/[^0-9]/g, '')) || 0;
    const formatCurrency = (val: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val);

    // Dynamic Calculations
    const totalAssets = neracaData.assets.current.reduce((acc: number, item: NeracaItem) => acc + parseCurrency(item.amount), 0) +
        neracaData.assets.nonCurrent.reduce((acc: number, item: NeracaItem) => acc + parseCurrency(item.amount), 0);

    const totalLiabilities = neracaData.liabilities.shortTerm.reduce((acc: number, item: NeracaItem) => acc + parseCurrency(item.amount), 0) +
        neracaData.liabilities.longTerm.reduce((acc: number, item: NeracaItem) => acc + parseCurrency(item.amount), 0);

    const totalEquity = neracaData.equity.base.reduce((acc: number, item: NeracaItem) => acc + parseCurrency(item.amount), 0);

    const totalLiabilitiesEquity = totalLiabilities + totalEquity;
    const isBalanced = totalAssets === totalLiabilitiesEquity;

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            {/* 1. Header Halaman */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pb-2">
                <div className="space-y-2">
                    <h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight">Neraca Keuangan Unit</h1>
                    <p className="text-secondary font-medium flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                        Posisi keuangan unit sekolah per tanggal tertentu
                    </p>
                </div>
                <div className="flex gap-3">
                    <button className="px-5 py-2.5 bg-surface border border-border-main rounded-xl text-[10px] font-bold text-muted hover:text-primary transition-all uppercase tracking-widest shadow-xs">
                        Download PDF
                    </button>
                    <button className="px-5 py-2.5 bg-primary text-white rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-primary/20 hover:primary-light transition-all">
                        Cetak Laporan
                    </button>
                </div>
            </div>

            {/* 2. Area Filter (Horizontal) */}
            <div className="bg-surface p-4 rounded-3xl border border-border-main shadow-sm grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                {/* Dropdown Unit */}
                <div className="md:col-span-4 flex items-center gap-3 px-3 py-2 bg-background border border-border-main rounded-2xl group hover:border-primary/30 transition-all">
                    <Building2 className="w-5 h-5 text-primary" />
                    <div className="flex-1">
                        <p className="text-[9px] font-black text-muted uppercase tracking-widest leading-none mb-1">Unit Sekolah</p>
                        <select
                            className="bg-transparent text-sm font-bold text-foreground outline-none w-full appearance-none cursor-pointer"
                            value={activeUnit}
                            onChange={(e) => setActiveUnit(e.target.value as SchoolUnit)}
                        >
                            {availableUnits.map(u => (
                                <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                            ))}
                        </select>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted" />
                </div>

                {/* Date Picker */}
                <div className="md:col-span-3 flex items-center gap-3 px-3 py-2 bg-background border border-border-main rounded-2xl group hover:border-primary/30 transition-all">
                    <Calendar className="w-5 h-5 text-warning" />
                    <div className="flex-1">
                        <p className="text-[9px] font-black text-muted uppercase tracking-widest leading-none mb-1">Tanggal Neraca</p>
                        <input
                            type="date"
                            className="bg-transparent text-sm font-bold text-foreground outline-none w-full cursor-pointer"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />
                    </div>
                </div>

                {/* Quick Filters */}
                <div className="md:col-span-5 flex items-center gap-2">
                    <span className="text-[9px] font-black text-muted uppercase tracking-widest ml-2 hidden lg:block">Quick Links:</span>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { id: 'sem1', label: 'Smt Ganjil' },
                            { id: 'sem2', label: 'Smt Genap' },
                            { id: 'year', label: 'Akhir Tahun' }
                        ].map((filter) => (
                            <button
                                key={filter.id}
                                onClick={() => setQuickFilter(filter.id)}
                                className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${quickFilter === filter.id
                                    ? 'bg-secondary text-white border-secondary'
                                    : 'bg-background border border-border-main text-muted hover:border-secondary/50'
                                    }`}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* 3. Ringkasan Neraca (3 Card) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { label: 'Total Aset', value: formatCurrency(totalAssets), icon: Wallet, color: 'text-primary', bg: 'bg-primary-soft', desc: 'Total kekayaan unit' },
                    { label: 'Total Kewajiban', value: formatCurrency(totalLiabilities), icon: Landmark, color: 'text-error', bg: 'bg-error-soft', desc: 'Total hutang & kewajiban' },
                    { label: 'Total Modal', value: formatCurrency(totalEquity), icon: Layers, color: 'text-success', bg: 'bg-success-soft', desc: 'Kekayaan bersih (Ekuitas)' }
                ].map((card, i) => (
                    <div key={i} className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm flex items-center gap-6 group hover:shadow-md transition-all">
                        <div className={`w-16 h-16 ${card.bg} ${card.color} rounded-3xl flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform`}>
                            <card.icon className="w-8 h-8" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-muted uppercase tracking-[0.2em] mb-1">{card.label}</p>
                            <h3 className="text-2xl font-black text-foreground tracking-tight">{card.value}</h3>
                            <p className="text-[9px] text-muted font-medium mt-1 uppercase tracking-widest">{card.desc}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* 4. Tabel Neraca (3 Kolom Utama) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* COLUMN: ASET */}
                <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col">
                    <div className="p-6 bg-primary border-b border-primary-dark">
                        <h4 className="text-white font-black uppercase tracking-[0.2em] flex items-center gap-3 text-sm">
                            <Building className="w-5 h-5 opacity-70" /> ASET (ASSETS)
                        </h4>
                    </div>
                    <div className="p-8 space-y-8 flex-1">
                        {/* Aset Lancar */}
                        <div className="space-y-4">
                            <span className="text-[9px] font-black text-primary uppercase tracking-widest bg-primary-soft px-2 py-1 rounded">Aset Lancar</span>
                            <div className="space-y-3 pl-1">
                                {neracaData.assets.current.map((item: NeracaItem, i: number) => (
                                    <div key={i} className="flex justify-between items-start gap-4 text-sm py-0.5">
                                        <span className="font-bold text-secondary leading-snug">{item.name}</span>
                                        <span className="font-black text-foreground tabular-nums whitespace-nowrap pt-0.5">{item.amount}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Aset Tidak Lancar */}
                        <div className="space-y-4 pt-4 border-t border-border-main/50">
                            <span className="text-[9px] font-black text-primary uppercase tracking-widest bg-primary-soft px-2 py-1 rounded">Aset Tidak Lancar</span>
                            <div className="space-y-3 pl-1">
                                {neracaData.assets.nonCurrent.map((item: NeracaItem, i: number) => (
                                    <div key={i} className="flex justify-between items-start gap-4 text-sm py-0.5">
                                        <span className="font-bold text-secondary leading-snug">{item.name}</span>
                                        <span className="font-black text-foreground tabular-nums whitespace-nowrap pt-0.5">{item.amount}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="p-6 bg-primary-soft/50 border-t border-border-main flex justify-between items-center">
                        <span className="text-[10px] font-black text-primary uppercase tracking-widest">Total Aset</span>
                        <span className="text-xl font-black text-foreground">{formatCurrency(totalAssets)}</span>
                    </div>
                </div>

                {/* COLUMN: KEWAJIBAN */}
                <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col">
                    <div className="p-6 bg-error border-b border-error-dark">
                        <h4 className="text-white font-black uppercase tracking-[0.2em] flex items-center gap-3 text-sm">
                            <Landmark className="w-5 h-5 opacity-70" /> KEWAJIBAN (LIABILITIES)
                        </h4>
                    </div>
                    <div className="p-8 space-y-8 flex-1">
                        {/* Jangka Pendek */}
                        <div className="space-y-4">
                            <span className="text-[9px] font-black text-error uppercase tracking-widest bg-error-soft px-2 py-1 rounded">Jangka Pendek</span>
                            <div className="space-y-3 pl-1">
                                {neracaData.liabilities.shortTerm.map((item: NeracaItem, i: number) => (
                                    <div key={i} className="flex justify-between items-start gap-4 text-sm py-0.5">
                                        <span className="font-bold text-secondary leading-snug">{item.name}</span>
                                        <span className="font-black text-foreground tabular-nums whitespace-nowrap pt-0.5">{item.amount}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Jangka Panjang */}
                        <div className="space-y-4 pt-4 border-t border-border-main/50">
                            <span className="text-[9px] font-black text-error uppercase tracking-widest bg-error-soft px-2 py-1 rounded">Jangka Panjang</span>
                            <div className="space-y-3 pl-1">
                                {neracaData.liabilities.longTerm.map((item: NeracaItem, i: number) => (
                                    <div key={i} className="flex justify-between items-start gap-4 text-sm py-0.5">
                                        <span className="font-bold text-secondary leading-snug">{item.name}</span>
                                        <span className="font-black text-foreground tabular-nums whitespace-nowrap pt-0.5">{item.amount}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="p-6 bg-error-soft/50 border-t border-border-main flex justify-between items-center">
                        <span className="text-[10px] font-black text-error uppercase tracking-widest">Total Kewajiban</span>
                        <span className="text-xl font-black text-foreground">{formatCurrency(totalLiabilities)}</span>
                    </div>
                </div>

                {/* COLUMN: MODAL */}
                <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col">
                    <div className="p-6 bg-success border-b border-success-dark">
                        <h4 className="text-white font-black uppercase tracking-[0.2em] flex items-center gap-3 text-sm">
                            <Layers className="w-5 h-5 opacity-70" /> MODAL (EQUITY)
                        </h4>
                    </div>
                    <div className="p-8 space-y-8 flex-1">
                        <div className="space-y-4">
                            <span className="text-[9px] font-black text-success uppercase tracking-widest bg-success-soft px-2 py-1 rounded">Ekuitas Unit</span>
                            <div className="space-y-4 pl-1">
                                {neracaData.equity.base.map((item: NeracaItem, i: number) => (
                                    <div key={i} className="flex justify-between items-start gap-4 text-sm py-0.5">
                                        <span className="font-bold text-secondary leading-snug">{item.name}</span>
                                        <span className="font-black text-foreground tabular-nums whitespace-nowrap pt-0.5">{item.amount}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="p-6 bg-success-soft/50 border-t border-border-main flex justify-between items-center">
                        <span className="text-[10px] font-black text-success uppercase tracking-widest">Total Modal</span>
                        <span className="text-xl font-black text-foreground">{formatCurrency(totalEquity)}</span>
                    </div>
                </div>
            </div>

            {/* 5. Validasi Neraca */}
            <div className={`p-6 rounded-3xl border-2 flex flex-col md:flex-row items-center gap-6 transition-all duration-500 ${isBalanced
                ? 'bg-success-soft/30 border-success/30'
                : 'bg-error-soft/30 border-error/30 animate-pulse'
                }`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm ${isBalanced ? 'bg-success text-white' : 'bg-error text-white'
                    }`}>
                    {isBalanced ? <CheckCircle2 className="w-8 h-8" /> : <AlertCircle className="w-8 h-8" />}
                </div>
                <div className="flex-1 space-y-1 text-center md:text-left">
                    <h5 className={`text-lg font-black uppercase tracking-wider ${isBalanced ? 'text-success' : 'text-error'}`}>
                        {isBalanced ? 'Neraca Seimbang (Balanced)' : 'Neraca Tidak Seimbang (Unbalanced)'}
                    </h5>
                    <p className="text-xs font-bold text-secondary uppercase tracking-widest">
                        Total Aset ({formatCurrency(totalAssets)}) = Total Kewajiban + Modal ({formatCurrency(totalLiabilitiesEquity)})
                    </p>
                </div>
                {!isBalanced && (
                    <div className="px-6 py-3 bg-white/50 border border-error/20 rounded-2xl text-xs font-black text-error uppercase tracking-widest">
                        Selisih: {formatCurrency(Math.abs(totalAssets - totalLiabilitiesEquity))}
                    </div>
                )}
            </div>
        </main>
    );
}
